import { useState, type FormEvent } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link, navigate, useLocation } from "@/lib/router";
import { SECTIONS } from "@/data/sections";
import { longDate } from "@/lib/format";
import { Ticker } from "./Ticker";

export function Logo({ className = "" }: { className?: string }) {
  return (
    <span className={`font-[family-name:var(--font-masthead)] font-black tracking-tight text-brand ${className}`}>
      BrickBharat
    </span>
  );
}

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const { pathname } = useLocation();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <>
    <header className="bg-white">
      {/* Utility bar */}
      <div className="border-b border-rule">
        <div className="mx-auto flex h-9 max-w-[1240px] items-center justify-between px-4 text-[12px] font-semibold uppercase tracking-wide text-ink-soft">
          <span className="hidden sm:inline">{longDate(new Date())}</span>
          <span className="sm:hidden">{new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
          <div className="flex items-center gap-4">
            <Link href="/section/cities" className="hidden hover:text-brand md:inline">Mumbai · Delhi · Bengaluru</Link>
            <a href="#newsletter" className="hover:text-brand">Newsletters</a>
            <a href="#newsletter" className="rounded-sm bg-brand px-2.5 py-1 text-white hover:bg-brand-dark">Subscribe</a>
          </div>
        </div>
      </div>

      {/* Masthead */}
      <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-3 px-4 py-3 md:py-5">
        <button aria-label="Open menu" onClick={() => setMenuOpen(true)} className="p-2 -ml-2 hover:text-brand">
          <Menu className="size-6" />
        </button>
        <Link href="/" aria-label="BrickBharat home" className="text-center leading-none">
          <Logo className="text-[2.6rem] sm:text-6xl md:text-7xl" />
          <span className="mt-1 hidden text-[11px] font-bold uppercase tracking-[0.3em] text-ink-soft sm:block">
            India's Real Estate News
          </span>
        </Link>
        <button aria-label="Search" onClick={() => setSearchOpen(s => !s)} className="p-2 -mr-2 hover:text-brand">
          {searchOpen ? <X className="size-6" /> : <Search className="size-6" />}
        </button>
      </div>

      {searchOpen && (
        <form onSubmit={submit} className="mx-auto flex max-w-[1240px] gap-2 px-4 pb-4">
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search BrickBharat: city, builder, RERA, home loans..."
            className="min-w-0 flex-1 border-2 border-ink px-3 py-2 text-base outline-none focus:border-brand"
          />
          <button className="bg-ink px-5 font-bold uppercase text-white hover:bg-brand">Go</button>
        </form>
      )}
    </header>

      {/* Section nav: outside <header> so it can stay stuck while scrolling */}
      <nav className="sticky top-0 z-30 border-y-[3px] border-ink bg-white" aria-label="Sections">
        <div className="no-scrollbar mx-auto flex max-w-[1240px] overflow-x-auto px-2">
          {SECTIONS.map(s => {
            const active = pathname === `/section/${s.id}`;
            return (
              <Link
                key={s.id}
                href={`/section/${s.id}`}
                className={`shrink-0 px-3 py-2.5 text-[14px] font-extrabold uppercase tracking-wide hover:text-brand ${active ? "text-brand" : ""}`}
              >
                {s.label}
              </Link>
            );
          })}
          <Link href="/video" className={`shrink-0 px-3 py-2.5 text-[14px] font-extrabold uppercase tracking-wide hover:text-brand ${pathname === "/video" ? "text-brand" : ""}`}>
            Video
          </Link>
        </div>
      </nav>

      <Ticker />

      {/* Slide-out menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <aside className="absolute left-0 top-0 flex h-full w-80 max-w-[85vw] flex-col overflow-y-auto bg-white shadow-xl">
            <div className="flex items-center justify-between border-b-[3px] border-ink px-4 py-3">
              <Logo className="text-3xl" />
              <button aria-label="Close menu" onClick={() => setMenuOpen(false)} className="p-1 hover:text-brand">
                <X className="size-6" />
              </button>
            </div>
            <form onSubmit={submit} className="flex gap-2 p-4">
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search"
                className="min-w-0 flex-1 border border-ink px-3 py-2 outline-none focus:border-brand"
              />
              <button aria-label="Search" className="bg-ink px-3 text-white hover:bg-brand">
                <Search className="size-4" />
              </button>
            </form>
            <ul className="px-4 pb-6">
              <li>
                <Link href="/" onClick={() => setMenuOpen(false)} className="block border-b border-rule py-3 text-lg font-extrabold uppercase hover:text-brand">
                  Home
                </Link>
              </li>
              {SECTIONS.map(s => (
                <li key={s.id}>
                  <Link
                    href={`/section/${s.id}`}
                    onClick={() => setMenuOpen(false)}
                    className="block border-b border-rule py-3 text-lg font-extrabold uppercase hover:text-brand"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link href="/video" onClick={() => setMenuOpen(false)} className="block border-b border-rule py-3 text-lg font-extrabold uppercase hover:text-brand">
                  Video
                </Link>
              </li>
            </ul>
          </aside>
        </div>
      )}
    </>
  );
}
